import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { docsAPI, fmtINR } from "@/lib/documents";
import { notifyDocument } from "./documentToast";

const METHODS = [["cash", "Cash"], ["card", "Card"], ["upi", "UPI"], ["bank_transfer", "Bank"], ["ota", "OTA"]];

/** Screen 1 — Collect Payment modal: records a PMS payment, optionally issues + opens the receipt. */
export function CollectPaymentDialog({ open, onClose, stay, folioId, summary, onDone }) {
  const [method, setMethod] = useState("card");
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (open) { setAmount(summary?.balance_due > 0 ? String(summary.balance_due) : ""); setReference(""); setMethod("card"); }
  }, [open, summary]);

  const submit = async (withReceipt) => {
    if (!(+amount > 0)) { toast.error("Enter an amount greater than zero"); return; }
    setBusy(withReceipt ? "receipt" : "pay");
    try {
      const { data: pay } = await api.post("/pms/payments/", { folio_id: folioId, method, amount: +amount, reference: reference || null });
      if (withReceipt) {
        const rec = await docsAPI.createReceipt({ payment_id: pay.id, receipt_type: "room" });
        notifyDocument(`Payment recorded · Receipt ${rec.receipt_number}`, { url: docsAPI.receiptPdfUrl(rec.id), filename: `Receipt-${rec.receipt_number}.pdf`, testId: "payment-receipt-toast" });
      } else {
        toast.success(`Payment ${pay.code} recorded`);
      }
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e) || "Payment failed"); }
    finally { setBusy(null); }
  };

  const ctx = summary || {};
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-hocp-bgSubtle border-hocp-border max-w-lg" data-testid="collect-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Collect Payment — Room Charges</DialogTitle>
          <DialogDescription className="text-[12px]">
            <span className="font-medium text-foreground">{stay?.guest_name}</span>
            {ctx.room_number ? ` · Room ${ctx.room_number}` : ""} · {stay?.expected_arrival} → {stay?.expected_departure}
          </DialogDescription>
        </DialogHeader>

        <div className="border border-hocp-border rounded-md overflow-hidden text-[12px]" data-testid="charge-summary">
          <div className="px-3 py-1.5 bg-hocp-bgCard text-[10px] mono uppercase tracking-wider text-hocp-textMuted">Charge summary</div>
          {[["Room charges (incl. tax)", ctx.room_total], ["Services", ctx.services_total], ctx.discounts ? ["Discounts", -ctx.discounts] : null, ["Already paid", -(ctx.amount_paid || 0)]].filter(Boolean).map(([k, v]) => (
            <div key={k} className="flex justify-between px-3 py-1.5 border-t border-hocp-border"><span className="text-muted-foreground">{k}</span><span className="mono">{fmtINR(v)}</span></div>))}
          <div className="flex justify-between px-3 py-2 border-t border-hocp-border font-semibold"><span>Balance due</span><span className="mono text-amber-400" data-testid="charge-summary-balance">{fmtINR(ctx.balance_due || 0)}</span></div>
        </div>

        <div>
          <Label className="text-[10px] uppercase tracking-wider text-hocp-textMuted">Payment method</Label>
          <div className="grid grid-cols-5 gap-1.5 mt-1.5" data-testid="payment-method-selector">
            {METHODS.map(([v, l]) => (
              <button key={v} type="button" onClick={() => setMethod(v)} data-testid={`method-${v}`}
                className={`h-9 rounded-md border text-[12px] font-medium transition-colors ${method === v ? "bg-white text-black border-white" : "border-hocp-border text-hocp-textPrimary hover:bg-hocp-bgSubtle"}`}>
                {method === v && <Check className="inline w-3.5 h-3.5 mr-1 -mt-0.5" strokeWidth={2} />}{l}
              </button>))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-[10px] uppercase tracking-wider text-hocp-textMuted">Amount *</Label><Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="pay-amount" /></div>
          <div><Label className="text-[10px] uppercase tracking-wider text-hocp-textMuted">Reference no.</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="TXN98234761" className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="pay-reference" /></div>
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="ghost" onClick={onClose} className="h-9 text-[12px]">Cancel</Button>
          <Button variant="outline" disabled={!!busy} onClick={() => submit(false)} className="h-9 text-[12px] bg-transparent border-hocp-border hover:bg-hocp-bgSubtle" data-testid="submit-payment">
            {busy === "pay" && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}Record payment only</Button>
          <Button disabled={!!busy} onClick={() => submit(true)} className="h-9 text-[12px] bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="confirm-payment-print-receipt">
            {busy === "receipt" ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}Confirm Payment & Print Receipt</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
